import { Event } from "event-system";
import { Ticker } from "pixi.js";
import { AnimationAction, AnimationClip, AnimationMixer, SkinnedMesh } from "three";
import { AnimationParsingUtils } from "../../../../../../three/AnimationParsingUtils";

export class MoleAnimationController {
    public readonly onClipEnd: Event<MoleAnimationState>;
    public readonly onAnimationProgress: Event<MoleAnimationState, number>;

    private readonly animationMixer: AnimationMixer;

    private bites: AnimationAction;
    private landing: AnimationAction;
    private climbsOut: AnimationAction;
    private climbsIn: AnimationAction;
    private walkingInPlace: AnimationAction;
    private death: Array<AnimationAction>;
    private idle: Array<AnimationAction>;
    private kick: Array<AnimationAction>;
    private jogging: AnimationAction;
    private throw: AnimationAction;

    private currentAnimationState: MoleAnimationState;
    private currentAnimationAction: AnimationAction;

    public constructor(
        moleMesh: SkinnedMesh,
        animations: AnimationClip[]
    ) {
        this.onClipEnd = new Event();
        this.onAnimationProgress = new Event();
        this.animationMixer = new AnimationMixer(moleMesh);
        this.parseMoleAnimations(animations);

        this.handleAnimationFinished = this.handleAnimationFinished.bind(this);

        this.animationMixer.addEventListener("finished", this.handleAnimationFinished);
        // Fix for skipping TPose
        this.currentAnimationAction = this.climbsOut.play();
        this.animationMixer.setTime(0.1);
        
        Ticker.shared.add(this.update, this);
    }

    public dispose(): void {
        Ticker.shared.remove(this.update, this);
        this.animationMixer.removeEventListener("finished", this.handleAnimationFinished);
    }

    public transitToState(next: MoleAnimationState): void {
        this.currentAnimationState = next;
        let nextAction: AnimationAction;
        switch (this.currentAnimationState) {
            case MoleAnimationState.Bites:
                nextAction = this.playBitesAnimation();
                break;
            case MoleAnimationState.Landing:
                nextAction = this.playLandingAnimation();
                break;
            case MoleAnimationState.WalkingInPlace:
                nextAction = this.playWalkingInPlaceAnimation();
                break;
            case MoleAnimationState.ClimbsOut:
                nextAction = this.playClimbsOutAnimation();
                break;
            case MoleAnimationState.ClimbsIn:
                nextAction = this.playClimbsInAnimation();
                break;
            case MoleAnimationState.Death:
                nextAction = this.playDeathAnimation();
                break;
            case MoleAnimationState.Idle:
                nextAction = this.playIdleAnimation();
                break;
            case MoleAnimationState.KickToTheGroin:
                nextAction = this.playKickAnimation();
                break;
            case MoleAnimationState.Jogging:
                nextAction = this.playJoggingAnimation();
                break;
            case MoleAnimationState.Throw:
                nextAction = this.playThrowAnimation();
                break;
        }
        nextAction.play();
        if (this.currentAnimationAction?.isRunning()) {
            if (this.currentAnimationAction != nextAction) {
                this.currentAnimationAction.crossFadeTo(nextAction, 0.2);
            }
        } else {
            this.currentAnimationAction?.crossFadeTo(nextAction, 0.2);
        }
        this.currentAnimationAction = nextAction;
    }

    public stop(): void {
        this.animationMixer.timeScale = 0;
    }

    public play(): void {
        this.animationMixer.timeScale = 1;
    }

    private parseMoleAnimations(animations: AnimationClip[]): void {
        this.bites = AnimationParsingUtils.animationActionByName(this.animationMixer, animations, "Bites");
        this.throwIfAnimationInvalid(this.bites, "bites");
        this.landing = AnimationParsingUtils.animationActionByName(this.animationMixer, animations, "Landing");
        this.throwIfAnimationInvalid(this.landing, "landing");
        this.climbsOut = AnimationParsingUtils.animationActionByName(this.animationMixer, animations, "ClimbsOut");
        this.throwIfAnimationInvalid(this.climbsOut, "cllimbsOut");
        this.climbsIn = AnimationParsingUtils.animationActionByName(this.animationMixer, animations, "ClimbsOut(revers)");
        this.throwIfAnimationInvalid(this.climbsOut, "climbsIn");
        this.walkingInPlace = AnimationParsingUtils.animationActionByName(this.animationMixer, animations, "Walking_in_place");
        this.throwIfAnimationInvalid(this.walkingInPlace, "walkingInPlace");
        this.death = AnimationParsingUtils.animationActionByCondition(this.animationMixer, animations, (clip) => clip.name.startsWith("Death"));
        this.throwIfAnimationInvalid(this.death, "death");
        this.idle = AnimationParsingUtils.animationActionByCondition(this.animationMixer, animations, (clip) => clip.name.startsWith("Idle") || clip.name.startsWith("Taunt"));
        this.throwIfAnimationInvalid(this.idle, "idle");
        this.kick = AnimationParsingUtils.animationActionByCondition(this.animationMixer, animations, (clip) => clip.name.startsWith("KickToTheGroin"));
        this.throwIfAnimationInvalid(this.kick, "kick");
        this.jogging = AnimationParsingUtils.animationActionByName(this.animationMixer, animations, "Run2(Jogging)");
        this.throwIfAnimationInvalid(this.jogging, "jogging");
        this.throw = AnimationParsingUtils.animationActionByName(this.animationMixer, animations, "Throw");
        this.throwIfAnimationInvalid(this.throw, "throw");
    }

    private throwIfAnimationInvalid(animation: Array<AnimationAction> | AnimationAction, name: string) {
        if (animation == null || Array.isArray(animation) && animation.length == 0) {
            throw new Error(`Mole has no ${name} animations configured. Check model you importing.`);
        }
    }

    private update(dt: number): void {
        this.animationMixer.update(dt / 50);
        if (this.currentAnimationAction?.isRunning()) {
            const clip = this.currentAnimationAction.getClip();
            this.onAnimationProgress.emit(this.currentAnimationState, this.currentAnimationAction.time / clip.duration);
        }
    }

    private handleAnimationFinished(eventData: { action: AnimationAction, direction: number }): void {
        if (eventData.action.getClip().name != this.currentAnimationAction.getClip().name) {
            return;
        }
        this.onClipEnd.emit(this.currentAnimationState);
    }

    private playBitesAnimation(): AnimationAction {
        return this.bites.reset();
    }

    private playLandingAnimation(): AnimationAction {
        return this.landing.reset();
    }

    private playWalkingInPlaceAnimation(): AnimationAction {
        return this.walkingInPlace.reset();
    }

    private playClimbsOutAnimation(): AnimationAction {
        return this.climbsOut.reset();
    }

    private playClimbsInAnimation(): AnimationAction {
        return this.climbsIn.reset();
    }

    private playDeathAnimation(): AnimationAction {
        return this.death.random().reset();
    }

    private playIdleAnimation(): AnimationAction {
        return this.idle.random().reset();
    }

    private playKickAnimation(): AnimationAction {
        return this.kick.random().reset();
    }

    private playJoggingAnimation(): AnimationAction {
        return this.jogging.reset();
    }

    private playThrowAnimation(): AnimationAction {
        return this.throw.reset();
    }

}

export enum MoleAnimationState {
    Bites,
    Landing,
    ClimbsOut,
    ClimbsIn,
    WalkingInPlace,
    Death,
    Idle,
    KickToTheGroin,
    PointingForward,
    PointingGesture,
    Jogging,
    Throw
}
